import { Group, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { type Updater, type PaginationState } from "@tanstack/react-table-v9";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { spacing } from "#app/theme/v2/spacing";
import { Button } from "#components/atoms/Button/Button";
import { Table } from "#components/organisms/Table/Table";
import { Page } from "#components/templates/Page/Page";
import { AllQuoteRequestsV2Dto } from "#modules/quoteRequestsV2/infra/fetchQuoteRequests.v2.api";
import { CreateQuoteRequestModal } from "#modules/quoteRequestsV2/view/organisms/CreateQuoteRequestModal";
import {
  DEFAULT_PER_PAGE,
  useQuoteRequestsV2,
} from "#modules/quoteRequestsV2/view/useQuoteRequestsV2";
import { useQuoteRequestsV2Columns } from "#modules/quoteRequestsV2/view/useQuoteRequestsV2Columns";
import { ROUTES } from "#shared/routes";
import { sortQuoteRequestLines } from "shared";

export const QUOTE_REQUEST_NAME_COLUMN_WIDTH = 480;

export type QuoteRequestsTableData = {
  id: string;
  reference: string | null;
  createdAt: Date;
  clientName: string | null;
  uniqueLines: AllQuoteRequestsV2Dto["quoteRequestLines"];
};

const LOADING_ROWS: QuoteRequestsTableData[] = Array.from(
  { length: DEFAULT_PER_PAGE },
  (_, index) => ({
    id: `loading-${index}`,
    reference: null,
    createdAt: new Date(0),
    clientName: null,
    uniqueLines: [],
  }),
);

const getUniqueQuoteRequestLines = (
  quoteRequestLines: AllQuoteRequestsV2Dto["quoteRequestLines"],
): AllQuoteRequestsV2Dto["quoteRequestLines"] =>
  Array.from(
    sortQuoteRequestLines(quoteRequestLines).batches.values(),
    (batch) => batch[0]!.quoteRequestLine,
  );

const QuoteRequestsPageV2Layout = ({ children }: { children: React.ReactNode }) => {
  const [opened, { open, close }] = useDisclosure(false);

  const { t } = useTranslation();

  return (
    <Page>
      <Page.Header />
      <Page.Content>
        <Group py={spacing[4]} justify="space-between" align="center">
          <Title order={2}>{t("quoteRequestsV2.title")}</Title>
          <Button onClick={open} leftSection={<IconPlus size={16} />}>
            {t("quoteRequestsV2.modal.title")}
          </Button>
        </Group>
        <CreateQuoteRequestModal opened={opened} onClose={close} />
        {children}
      </Page.Content>
    </Page>
  );
};

export const QuoteRequestsPageV2 = () => {
  const navigate = useNavigate();
  const { quoteRequests, metadata, setPagination, isFetching, page } = useQuoteRequestsV2();
  const columns = useQuoteRequestsV2Columns(isFetching);

  const quoteRequestsTableData = quoteRequests.map((quoteRequest) => ({
    id: quoteRequest.id,
    reference: quoteRequest.reference,
    createdAt: quoteRequest.createdAt,
    clientName: quoteRequest.client?.name ?? null,
    uniqueLines: getUniqueQuoteRequestLines(quoteRequest.quoteRequestLines),
  }));

  const handleRowClick = (row: QuoteRequestsTableData) => {
    navigate(ROUTES.v2QuoteRequest(row.id));
  };

  const pagination = {
    pageIndex: page - 1,
    pageSize: DEFAULT_PER_PAGE,
  };

  const handlePaginationChange = (updater: Updater<PaginationState>) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPagination({ page: next.pageIndex + 1 });
  };

  return (
    <QuoteRequestsPageV2Layout>
      <Table
        enableRowSelection={false}
        columns={columns}
        data={isFetching ? LOADING_ROWS : quoteRequestsTableData}
        enableSorting={false}
        enablePagination
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        pageCount={metadata?.pageCount}
        getRowId={(row) => row.id}
        onRowClick={isFetching ? undefined : handleRowClick}
        loading={isFetching}
      />
    </QuoteRequestsPageV2Layout>
  );
};
